import http from '../utils/http';
import axios from 'axios';
import { IApplicationState } from '../reducers';
import actionTypes from '../actions/actionTypes';
import { AxiosResponse } from 'axios';
import { QTYPES, Question, QuestionSummary } from '@nexxt/common/types';
import { checkProjectHealth } from './project.service';
import config from '../config';
import { getPrice } from './payment.service';
import { flattenQuestionSummary, getAdjacentQuestion } from '../utils/question';
import { GROUP_QTYPES } from '../constants/question';
const { api } = config;

/**
 *
 * GET - Call question list and save question flow in SortableQuestions and SortableQuestionObjects
 * @param projectId
 */
export function getSortedQuestionByProjectId(projectId: number) {
    return async (dispatch, getState: () => IApplicationState) => {
        const tenantID = localStorage.getItem('tenantId');
        const currentLockUser = getState().projectReducer.currentLockUser;
        const editEnabled = currentLockUser.isValid && currentLockUser.userId;
        try {
            const url = `/api/projects/${projectId}/flow?tenantID=${tenantID}`;
            const response = await http.get(url);
            dispatch({
                type: actionTypes.GET_ALL_QUESTIONS_FLOW,
                payload: {
                    projectId,
                    questions: response.data
                }
            });
            if (editEnabled) {
                await dispatch(
                    saveUpdatedSortedQuestion(
                        projectId,
                        getState().questionReducer.questionSummaries[projectId]
                    )
                );
            } else {
                await dispatch(checkProjectHealth(projectId));
                await dispatch(getPrice({ projectID: projectId }));
            }
            return response.data;
        } catch (e) {
            // Remove all questions from list if error occurs
            // @TODO: Decide if this is not required
            dispatch({
                type: actionTypes.GET_ALL_QUESTIONS_FLOW,
                payload: {
                    projectId,
                    questions: []
                }
            });
            throw e;
        }
    };
}

/**
 * POST - save question flow from SortableQuestions and return updated list.
 * @param projectId
 * @param sortableQuestions
 */
export function saveUpdatedSortedQuestion(
    projectId: number,
    questionSummaries?: QuestionSummary[]
) {
    return async (dispatch, getState: () => IApplicationState) => {
        const tenantID = localStorage.getItem('tenantId');
        const questions =
            questionSummaries ||
            getState().questionReducer.questionSummaries[projectId];
        const openCurrentQuestion =
            getState().questionReducer.currentQuestion || {};
        try {
            const url = `/api/projects/${projectId}/flow?tenantID=${tenantID}`;
            const response = await http.put(url, questions);
            // add dispatch to add to current queue; or use state from current file
            dispatch({
                type: actionTypes.AFTER_SAVE_SORT_QUESTIONS,
                payload: {
                    projectId,
                    questions
                }
            });

            dispatch(checkProjectHealth(projectId));
            dispatch(getPrice({ projectID: projectId }));
            return projectId;
        } catch (e) {
            // Remove all questions from list if error occurs
            // @TODO: Decide if this is not required
            console.log(e);
            throw e;
        }
    };
}

/**
 * Fetch when a user drag-n-drop the question and automatically call saveUpdatedSortedQuestion function to save a flow
 * @param projectId
 * @param dragIndex
 * @param hoverIndex
 * @param questionId
 */
export function sortQuestions(
    projectId: number,
    dragIndex: number,
    hoverIndex: number,
    questionId: number
) {
    return async (dispatch, getState: () => IApplicationState) => {
        if (questionId && questionId > 0) {
            await dispatch({
                type: actionTypes.SORT_QUESTIONS_BY_GROUP,
                payload: {
                    projectId,
                    dragIndex,
                    hoverIndex,
                    questionId
                }
            });
        } else {
            await dispatch({
                type: actionTypes.SORT_QUESTIONS,
                payload: {
                    projectId,
                    dragIndex,
                    hoverIndex,
                    questionId
                }
            });
        }
        await dispatch(saveUpdatedSortedQuestion(projectId));
    };
}

/**
 * Edit greeting message
 * Set currentQuestion type greeting and isEditQuestion true
 */
export function editGreeting(questionType: QTYPES) {
    return async (dispatch, getState: () => IApplicationState) => {
        dispatch({
            type: actionTypes.EDIT_GREETING,
            payload: { questionType }
        });
    };
}

/**
 * Preview a question
 * @param projectId
 * @param questionId
 */
export function previewQuestion(projectId: number, questionId: number) {
    return async (dispatch, getState: () => IApplicationState) => {
        const url = `${api.endpoints.project}/${projectId}/preview/${questionId}`;
        const response = await http.post(url);
        dispatch({
            type: actionTypes.PREVIEW_QUESTION,
            payload: {
                previewPackageId: response.data.packageID
            }
        });
    };
}

/**
 * GET - returns question details by question id.
 * @param questionId
 * @param options { logic, logicList }
 * logic - when a user click a logic button to set a condition
 * logicList - set true only when API called to fetch question details from condition list.
 */
export function getQuestionByQuestionId(questionId: number, options?: any) {
    return async (
        dispatch,
        getState: () => IApplicationState
    ): Promise<Question> => {
        try {
            const url = `/api/questions/${questionId}`;
            const response = await http.get(url);
            // add dispatch to add to current queue; or use state from current file
            if (options && options.logicList) {
                return response.data;
            } else if (options && options.logic) {
                dispatch({
                    type: actionTypes.SET_LOGIC_QUESTION,
                    payload: {
                        questionId,
                        question: response.data
                    }
                });
            } else if (options && options.quotas) {
                dispatch({
                    type: actionTypes.SET_SAMPLE,
                    payload: {
                        questionId,
                        question: response.data
                    }
                });
            } else if (options && options.edit) {
                dispatch({
                    type: actionTypes.SET_EDIT_QUESTION,
                    payload: {
                        questionId,
                        question: response.data
                    }
                });
            } else {
                dispatch({
                    type: actionTypes.SET_ACTIVE_QUESTION,
                    payload: {
                        questionId,
                        question: response.data
                    }
                });
            }
            return response.data;
        } catch (e) {
            // Remove all questions from list if error occurs
            // @TODO: Decide if this is not required
            console.log(e);
            if ((options && options.logic) || (options && options.logicList)) {
                dispatch({
                    type: actionTypes.SET_LOGIC_QUESTION,
                    payload: {
                        question: {}
                    }
                });
            } else {
                dispatch({
                    type: actionTypes.SET_EDIT_QUESTION,
                    payload: {
                        questionId,
                        question: {}
                    }
                });
            }
            throw e;
        }
    };
}

/**
 *  POST - Create a new question
 *  QTYPES.POPUP - sending a custom API(api/popup)
 * @param question
 * @param projectId
 * @param entityId
 */
export function createQuestion(
    question: any,
    projectId: number,
    entityId = -1
) {
    return async (dispatch: any, getState: () => IApplicationState) => {
        const tenantID = localStorage.getItem('tenantId');
        const state = getState();
        if (entityId === -1) {
            entityId = (
                state.questionReducer.questionSummaries[projectId] || []
            ).length;
        }
        const data = {
            ...question,
            projectID: projectId,
            entityID: entityId,
            position: question.position >= 0 ? question.position : entityId
        };
        if (data.type === QTYPES.POPUP) {
            try {
                const url = `/api/popup?tenantID=${tenantID}`;
                const response = await http.post(url, data);
                const { question, questionSummary } = response.data.data;
                dispatch({
                    type: actionTypes.CREATE_QUESTIONS,
                    payload: { projectId, question, questionSummary }
                });

                await dispatch(
                    saveUpdatedSortedQuestion(
                        projectId,
                        getState().questionReducer.questionSummaries[projectId]
                    )
                );

                return { ...question };
            } catch (e) {
                throw e;
            }
        } else {
            try {
                const url = `/api/questions?tenantID=${tenantID}`; // suggest sending project Id as well
                const response = await http.post(url, data);
                const { question, questionSummary } = response.data.data;
                // add dispatch to add to current queue; or use state from current file
                dispatch({
                    type: actionTypes.CREATE_QUESTION,
                    payload: { projectID: projectId, question, questionSummary }
                });
                await dispatch(
                    saveUpdatedSortedQuestion(projectId),
                    getState().questionReducer.questionSummaries[projectId]
                );
                return { ...question };
            } catch (e) {
                // show error
                throw e;
            }
        }
    };
}

/**
 * PUT - update question
 * QTYPES.POPUP - sending a custom API(api/popup)
 * @param question
 */
export function updateQuestion(question: any) {
    return async (dispatch, getState: () => IApplicationState) => {
        const projectID = getState().projectReducer.currentProject.id;
        const tenantID = localStorage.getItem('tenantId');
        if (question.type === QTYPES.POPUP) {
            try {
                const url = `/api/popup/${question.id}?tenantID=${tenantID}`;
                const response: AxiosResponse<any> = await http.put(url, {
                    ...question
                });
                const { question: updatedQuestion, questionSummary } =
                    response.data.data;
                dispatch({
                    type: actionTypes.UPDATE_QUESTION,
                    payload: {
                        projectID,
                        question: {
                            ...updatedQuestion,
                            deleteOptionIDs: [],
                            deleteCardIds: [],
                            projectID
                        },
                        questionSummary
                    }
                });

                await dispatch(
                    saveUpdatedSortedQuestion(
                        projectID,
                        getState().questionReducer.questionSummaries[projectID]
                    )
                );
                return {
                    ...updatedQuestion,
                    deleteOptionIDs: [],
                    deleteCardIds: []
                };
            } catch (e) {
                throw e;
            }
        } else {
            try {
                const url = `/api/questions/${question.id}?tenantID=${tenantID}`; // suggest sending project Id as well
                const response: AxiosResponse<any> = await http.put(url, {
                    ...question
                });
                const { question: updatedQuestion, questionSummary } =
                    response.data.data;
                // add dispatch to add to current queue; or use state from current file
                dispatch({
                    type: actionTypes.UPDATE_QUESTION,
                    payload: {
                        projectID,
                        question: {
                            ...updatedQuestion,
                            deleteOptionIDs: [],
                            projectID
                        },
                        questionSummary
                    }
                });
                dispatch(checkProjectHealth(projectID));

                return { ...updatedQuestion, deleteOptionIDs: [] };
            } catch (e) {
                // show error
                throw e;
            }
        }
    };
}

/**
 * POST - duplicate the question and add it at the end of question list
 * @param question
 * @param entityId
 */
export function duplicateQuestion(question, entityId = -1) {
    return async (dispatch, getState: () => IApplicationState) => {
        const projectID = getState().projectReducer.currentProject.id;
        const tenantID = localStorage.getItem('tenantId');
        try {
            const url = `/api/questions/${question.id}/duplicate`; // suggest sending project Id as well
            const response: AxiosResponse<any> = await http.post(url, {
                newTitle: question.questionTitle,
                tenantID,
                newProjectID: projectID
            });
            const { question: newQuestion, questionSummary } = response?.data;
            // add dispatch to add to current queue; or use state from current file
            dispatch({
                type: actionTypes.DUPLICATE_QUESTION,
                payload: {
                    projectID,
                    question: {
                        ...newQuestion,
                        deleteOptionIDs: [],
                        projectID
                    },
                    questionSummary
                }
            });

            await dispatch(
                saveUpdatedSortedQuestion(
                    projectID,
                    getState().questionReducer.questionSummaries[projectID]
                )
            );
            return { ...response.data.newEntity, deleteOptionIDs: [] };
        } catch (e) {
            // show error
            throw e;
        }
    };
}

/**
 * DELETE - Delete question by question id
 * @param question
 */
export function deleteQuestion(question: any, isParent: boolean) {
    return async (dispatch, getState: () => IApplicationState) => {
        try {
            const url = `/api/questions/${question.id}`; // suggest sending project Id as well
            const { currentProject } = getState().projectReducer;
            const { id: projectId } = currentProject;
            const { currentQuestion, currentQuestionId, questionSummaries } =
                getState().questionReducer;

            const renderQuestionId = getAdjacentQuestion(
                question,
                questionSummaries[projectId]
            );
            // Forcibly re-render if the question selected is a child of the question being deleted
            let forceRerender = false;
            if (GROUP_QTYPES.includes(question.type)) {
                if (question.type === QTYPES.POPUP) {
                    currentQuestionId !== currentQuestion.id &&
                        (forceRerender = true);
                } else {
                    currentQuestion.parent === question.id &&
                        (forceRerender = true);
                }
            }

            const questionIds = [
                question.id,
                ...flattenQuestionSummary(question.childrenSummaries).map(
                    (child) => child.id
                )
            ];
            await http.delete(url, {
                data: {
                    isParent,
                    isAudience: question.isAudience,
                    questionIds
                }
            });
            // // add dispatch to add to current queue; or use state from current file
            dispatch({
                type: actionTypes.DELETE_QUESTION,
                payload: {
                    questionID: question.id,
                    projectID: projectId
                }
            });
            await dispatch(
                saveUpdatedSortedQuestion(
                    projectId,
                    getState().questionReducer.questionSummaries[projectId]
                )
            );
            if (currentQuestionId === question.id || forceRerender) {
                if (renderQuestionId === -1) {
                    await dispatch(editGreeting(QTYPES.GREETING_TEXT));
                } else {
                    await dispatch(
                        getQuestionByQuestionId(renderQuestionId, {
                            edit: true
                        })
                    );
                }
            }
            return question.id;
        } catch (e) {
            throw e;
        }
    };
}

/**
 * Cancel edit question. Update isEditQuestion
 */
export function cancelEditQuestion() {
    return async (dispatch, getState: () => IApplicationState) => {
        const { id: projectID } = getState().projectReducer.currentProject;
        const { questionSummaries, currentQuestion } =
            getState().questionReducer;
        if (
            questionSummaries[projectID].length === 0 ||
            currentQuestion.position === 0
        ) {
            await dispatch(editGreeting(QTYPES.GREETING_TEXT));
        } else if (currentQuestion.position === -1) {
            // If Add new question button was used will redirect to the last question (above redirect)
            await dispatch(
                getQuestionByQuestionId(
                    Number(
                        questionSummaries[projectID][
                            questionSummaries[projectID].length - 1
                        ].id
                    ),
                    {
                        edit: true
                    }
                )
            );
        } else {
            //If insert question here was used will redirect to the nearest adjacent question
            const newCurrentQuestionId = getAdjacentQuestion(
                currentQuestion,
                questionSummaries[projectID]
            );
            await dispatch(
                getQuestionByQuestionId(newCurrentQuestionId, {
                    edit: true
                })
            );
        }
    };
}

export function getConditions(questionId) {
    return async (dispatch, getState) => {
        try {
            const tenantID = localStorage.getItem('tenantId');
            const url = `/api/questions/${questionId}/conditions`;
            const response = await http.get(url);
            return response.data;
        } catch (e) {
            console.log(e);
            return [];
        }
    };
}

/**
 * POST - save new condition
 * @param questionId
 * @param conditions
 * @param isPrecondition
 */
export function saveConditions(questionId, conditions, isPrecondition = false) {
    return async (dispatch, getState: () => IApplicationState) => {
        try {
            const tenantID = localStorage.getItem('tenantId');
            const url = `/api/questions/${questionId}/conditions?tenantID=${tenantID}`; // suggest sending project Id as well
            const response = await http.post(url, {
                prepost: !isPrecondition,
                conditions
            });
            await dispatch(
                getSortedQuestionByProjectId(
                    getState().projectReducer.currentProject.id
                )
            );

            return response.data;
        } catch (e) {
            console.log(e);
            return [];
        }
    };
}

/**
 * Delete the condition by ConditionID
 * @param questionId
 * @param deletedConditionIDs
 */
export function deleteConditions(questionId, deletedConditionIDs) {
    return async (dispatch, getState) => {
        try {
            const tenantID = localStorage.getItem('tenantId');
            const url = `/api/questions/${questionId}/conditions?tenantID=${tenantID}`; // suggest sending project Id as well
            const response = await http.delete(url, {
                data: {
                    deletedConditionIDs
                }
            });
            await dispatch(
                getSortedQuestionByProjectId(
                    getState().projectReducer.currentProject.id
                )
            );
            return response.data;
        } catch (e) {
            console.log(e);
            return [];
        }
    };
}

/*
 * Generate a set of object detection metadata for specified mediaURL, using computer vision API.
 * @TODO: add more documentation
 */
export async function getObjectDetect(mediaURL) {
    try {
        const url = 'api/questions/annotate/computer-vision';
        const response = await http.post(url, { mediaURL });

        return response.data;
    } catch (e) {
        console.error(e);
        return [];
    }
}

/*
 * Generate a set of image URLs required for QTYPES.GUIDED_FANTASY, based on the provided `logo_url`
 * @DOC: https://nlp.inca.chat/docs#/Utilities/gf_images_utils_gfimages_post
 */
export async function generateCombinedImages(logo_url: string) {
    try {
        const url = `${process.env.PYTHON_MICROSERVICE}/guided_fantasy_image`;

        const response = await axios.post(url, { logo_url });

        return response.data;
    } catch (err) {
        return {};
    }
}
